import { useMemo, type ReactNode } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ThemeProvider, alpha } from "@mui/material/styles";
import { useBranding } from "../../branding/BrandingProvider";
import { brandPrefersDark, getBrandThemes } from "../../theme";
import { translucentSurface } from "../../theme/surfaces";
import { Copyright } from "../Layout/Copyright";

/**
 * Links and accent copy on these pages use the theme's text-contrast accent (`brand.accentStrong`,
 * ≥ 4.5:1 on paper): the bright teal on the navy, and on white the brand blue (#2A6D92, 5.7:1),
 * because there neither teal clears WCAG AA for body text (primary.dark #3A8F88 is 3.9:1).
 */
export const AUTH_LINK_COLOR = "brand.accentStrong";

interface AuthPageFrameProps {
  /** The small spaced-out label above the heading ("Welcome back", "One more step"). */
  eyebrow: string;
  title: string;
  /** Space under the heading, in theme spacing units. */
  titleGap?: number;
  children: ReactNode;
}

/**
 * The frame every screen before the app shell shares — sign in, two-factor setup, forgot/reset
 * password, secure-your-account: the brand's ambient field, its logo, and a raised frosted card
 * with an eyebrow + heading. One place, so those screens read as one flow rather than the login
 * page and four plain white cards (2026-10-20).
 *
 * The frame is fixed to the brand's own mode, regardless of the app's light/dark preference.
 * CerebraLinked: the deep-navy hero is the brand's signature look and lets its logo blend
 * seamlessly. TeleHeadache: light, on the full-colour logo (Derek, 2026-10-02).
 */
export default function AuthPageFrame({
  eyebrow,
  title,
  titleGap = 3.5,
  children,
}: AuthPageFrameProps) {
  const brand = useBranding();
  const isDark = brandPrefersDark(brand.key);
  const pageTheme = useMemo(
    () =>
      isDark ? getBrandThemes(brand.key).dark : getBrandThemes(brand.key).light,
    [brand.key, isDark],
  );
  // Dark: prefer the full-color hero lockup, falling back to the white logo. Light: the standard
  // logo, which is the one drawn for a white background.
  const heroLogo = isDark
    ? (brand.heroLogo ?? brand.logoWhite ?? brand.logo)
    : brand.logo;

  return (
    <ThemeProvider theme={pageTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          // A minimum gutter, not the visual gap: the column is centred, so on a tall screen the
          // free space above the logo comes from centring and this value never shows. It only
          // takes effect once the content is taller than the viewport — exactly when padding is
          // the last thing you want, because it pushes Sign In below the fold. At 8 the page
          // overflowed a 823px viewport by 119px while showing 74px of dead space up top.
          py: { xs: 2.5, sm: 3 },
          position: "relative",
          overflow: "hidden",
          // The theme's ambient glow (soft teal/blue pooled in the corners, so the center stays
          // clean for the logo + card); "none" on a theme that has no glow.
          backgroundColor: "background.default",
          backgroundImage: (theme) => theme.palette.brand.ambient,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 460,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Brand hero lockup — a transparent PNG that blends into the field behind it. */}
          <Box
            component="img"
            src={heroLogo}
            alt={`${brand.displayName} logo`}
            sx={{
              width: "100%",
              // The lockup is a tall stack (brain, wordmark, tagline), so it costs more vertical
              // room than its visual weight suggests; sized so the whole sign-in column fits a
              // laptop viewport. The asset itself is cropped tight to the artwork, so this width
              // is the width you actually see — it used to include a ~30px transparent border on
              // every side, which quietly shrank the logo and padded the gap above it.
              // The TeleHeadache logo is a wider, shorter mark and reads large at the same width.
              maxWidth: isDark ? { xs: 260, sm: 300 } : { xs: 200, sm: 240 },
              height: "auto",
              mb: { xs: 2.5, sm: 3 },
              filter: (theme) =>
                isDark
                  ? `drop-shadow(0 6px 26px ${alpha(theme.palette.brand.accent, 0.28)})`
                  : "none",
              animation: "clFadeIn 700ms ease-out both",
              "@keyframes clFadeIn": {
                from: { opacity: 0, transform: "translateY(-8px)" },
                to: { opacity: 1, transform: "none" },
              },
            }}
          />

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 3, sm: 4.5 },
              borderRadius: 4,
              // A frosted-glass card over the page's ambient glow (near-opaque paper where the
              // theme has none).
              bgcolor: (theme) => translucentSurface(theme, 0.72),
              backdropFilter: "blur(16px)",
              border: (theme) =>
                `1px solid ${alpha(theme.palette.brand.accent, 0.2)}`,
              boxShadow: (theme) => theme.palette.brand.shadow.raised,
              animation: "clRise 700ms ease-out 120ms both",
              "@keyframes clRise": {
                from: { opacity: 0, transform: "translateY(14px)" },
                to: { opacity: 1, transform: "none" },
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                display: "block",
                textAlign: "center",
                color: AUTH_LINK_COLOR,
                letterSpacing: "0.22em",
                mb: 0.5,
              }}
            >
              {eyebrow}
            </Typography>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                mb: titleGap,
                textAlign: "center",
              }}
            >
              {title}
            </Typography>

            {children}
          </Paper>

          <Box sx={{ mt: 4, opacity: 0.75 }}>
            <Copyright />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
